// ═══════════════════════════════════════════════════════════════
// Documents.jsx — verticalstack XC-DOC Document Vault
// Path: src/components/Documents.jsx
//
// S5.B (May 18, 2026) — NEW FILE. Ports sandbox v7.2 xcDocuments()
// function (lines 7290-7368) from vanilla-JS HTML idiom to React JSX.
// First production consumer of XcFilterBar (S4 component).
// First unlocked Cross-cutting nav item (ShellNav xc-documents flip).
//
// S5.B rev (May 18, 2026, post-review fixes):
//   - Bug #1 fix: removed dead useFilterState call (XcFilterBar renders
//     its own filter UI; filter-value-to-table-filter logic deferred per
//     AS-S5-013 — out of S5 scope, lands in T3 process activations).
//   - Bug #3 fix: getAuth now statically imported at top (was dynamic
//     await import('firebase/auth') inside handleUpload).
//
// CANONICAL SCHEMA (S5 close handoff Section 6a)
//   /documents collection field set:
//   Required: id, workspaceId, uploadedBy, uploadedAt, fileName,
//             storagePath, storageUrl, docType, mediaKind,
//             processingStatus, accessRoles[]
//   Optional: parcelId, stageHint, signatureStatus, isParcelHero,
//             processedBy, ocrText, extractedFields, aiClassification,
//             originalFilename, linkedTo[], tags[], capturedAt,
//             gpsLat, gpsLng, widthPx, heightPx, durationSec
//
// MEDIA MODEL (Option A two-track)
//   Parcel.img + Parcel.photos[] + Parcel.videos[] remain as display
//   cache for hero/gallery rendering (no UI regression on Portfolio.jsx
//   or Overview.jsx). /documents is the system of record for queryable
//   metadata (OCR, classification, stage filtering, status workflow).
//
// AI WIRING (S5.C)
//   AI-08 OCR (ocrDocument CF, deployed): fires post-upload on every
//   file, populates ocrText + extractedFields, advances processingStatus
//   Created → Processed.
//   AI-26 classifier: deferred per AS-S5-004 if CF not deployed.
//   KPI card shows "AI-26 pending" placeholder until classifier ships.
//
// SMOKE HARNESS
//   mockMode prop (boolean): when true, bypasses Firestore read and
//   renders with hardcoded 4-doc mock array spanning stages. Used by
//   PlaceholderDashboard ?xcdoc-smoke=1 harness.
//
// FILTER WIRING (deferred per AS-S5-013)
//   XcFilterBar renders the filter chip UI but its values are NOT used
//   to filter the docs table in S5.B. Filter-to-table wiring requires
//   per-view filter-field semantic mapping (entity / type / stage /
//   status / parcel) which is more cleanly handled when discrete
//   process activations land in T3 (S13+). KPI cards and table render
//   all workspace docs unfiltered in Phase 1.
//
// AS REFERENCES
//   AS-S5-001 (rev) — canonical schema with parcelId/docType/accessRoles
//   AS-S5-003 — sandbox port with functional Upload
//   AS-S5-004 — AI-26 deferred handling
//   AS-S5-006 — mockMode smoke harness pattern
//   AS-S5-013 — filter wiring deferred (XcFilterBar renders, values
//               not consumed for table filtering in S5.B)
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import {
  collection, query, where, getDocs, addDoc, serverTimestamp, orderBy,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { db } from '../firebase/config';
import XcFilterBar from './xcFilterBar';

// ── Firebase instances (derived from existing app — works regardless of
// whether firebase/config exports storage/functions/auth) ────────────────
const app = getApp();
const storage = getStorage(app);
const functions = getFunctions(app);
const auth = getAuth(app);

// ── Mock data for smoke harness (4 docs spanning stages) ─────────────────
const MOCK_DOCS = [
  {
    id: 'mock-1',
    fileName: 'Purchase_Agreement_Yost_Rd.pdf',
    docType: 'Purchase Agreement',
    mediaKind: 'document',
    parcelId: 'rop-yost-rd',
    parcelLabel: 'Yost Rd',
    stageHint: 'acquire',
    processingStatus: 'Processed',
    signatureStatus: 'awaiting',
    uploadedAt: '2026-05-12',
  },
  {
    id: 'mock-2',
    fileName: 'Draw_Request_3_Liberty_Commons.pdf',
    docType: 'Draw Request',
    mediaKind: 'document',
    parcelId: 'rop-liberty-commons',
    parcelLabel: 'Liberty Commons',
    stageHint: 'develop',
    processingStatus: 'Filed',
    signatureStatus: 'submitted',
    uploadedAt: '2026-05-10',
  },
  {
    id: 'mock-3',
    fileName: 'Lease_Tony_Wade_NNN.pdf',
    docType: 'Lease',
    mediaKind: 'document',
    parcelId: 'rop-tile-factory-tx',
    parcelLabel: 'Potters NNN',
    stageHint: 'operate',
    processingStatus: 'Linked',
    signatureStatus: 'signed',
    uploadedAt: '2021-12-02',
  },
  {
    id: 'mock-4',
    fileName: 'hero.jpg',
    docType: 'photo',
    mediaKind: 'photo',
    parcelId: 'a1-sfh-01',
    parcelLabel: '1352 Garfield Ave SW',
    stageHint: 'operate',
    processingStatus: 'Linked',
    signatureStatus: null,
    isParcelHero: true,
    uploadedAt: '2026-04-28',
  },
];

// ── Status pill color map (per sandbox v7.2 + canonical 5-state addition) ─
function statusPillColors(status) {
  // signatureStatus values from sandbox
  if (status === 'signed')    return { fg: '#0A5C42', bg: '#E8F7F2' };
  if (status === 'final')     return { fg: '#1A4F7A', bg: '#EBF4FD' };
  if (status === 'draft')     return { fg: '#5A6478', bg: '#F0F2F5' };
  if (status === 'submitted') return { fg: '#7A4A08', bg: '#FDF3E3' };
  if (status === 'awaiting')  return { fg: '#7A1F15', bg: '#FDEEEC' };
  // processingStatus values from canonical 5-state (SHARED-COMPONENTS)
  if (status === 'Created')   return { fg: '#5A6478', bg: '#F0F2F5' };
  if (status === 'Processed') return { fg: '#1A4F7A', bg: '#EBF4FD' };
  if (status === 'Filed')     return { fg: '#1A4F7A', bg: '#EBF4FD' };
  if (status === 'Linked')    return { fg: '#0A5C42', bg: '#E8F7F2' };
  if (status === 'Archived')  return { fg: '#9CA3AF', bg: '#F3F4F6' };
  return { fg: '#5A6478', bg: '#F0F2F5' };
}

// ── Inline styles (matches DiscreteProcessUI / xcFilterBar conventions) ──
const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },
  banner: {
    backgroundColor: '#E6F4F8',
    border: '1px solid rgba(0, 184, 208, 0.25)',
    borderRadius: 8,
    padding: '12px 18px',
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  bannerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#003060',
  },
  bannerTag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 700,
    color: '#006B7D',
    background: 'rgba(0, 184, 208, 0.15)',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontFamily: 'Consolas, monospace',
  },
  bannerMuted: {
    fontSize: 12,
    color: '#6B7280',
  },
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 18,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#6B7280',
    margin: 0,
  },
  pageActions: {
    display: 'flex',
    gap: 8,
  },
  btnGhost: {
    padding: '8px 14px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhostDisabled: {
    padding: '8px 14px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#9CA3AF',
    cursor: 'not-allowed',
    fontFamily: 'inherit',
  },
  hiddenFileInput: {
    display: 'none',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  kpiCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 14,
  },
  kpiCardAccent: {
    backgroundColor: '#FFFFFF',
    border: '1px solid rgba(0, 184, 208, 0.3)',
    borderLeft: '3px solid #00B8D0',
    borderRadius: 8,
    padding: 14,
  },
  kpiCardGood: {
    backgroundColor: '#FFFFFF',
    border: '1px solid rgba(10, 92, 66, 0.3)',
    borderLeft: '3px solid #0A5C42',
    borderRadius: 8,
    padding: 14,
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  kpiVal: {
    fontSize: 26,
    fontWeight: 700,
    color: '#003060',
    lineHeight: 1.1,
  },
  kpiSub: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 4,
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  cardHead: {
    padding: '14px 18px',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#FAFBFC',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
  },
  cardSub: {
    fontSize: 12,
    color: '#6B7280',
    margin: 0,
    marginTop: 2,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid #F3F4F6',
    fontSize: 13,
    color: '#374151',
  },
  emptyRow: {
    textAlign: 'center',
    padding: 20,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  mono: {
    fontFamily: 'Consolas, monospace',
    fontSize: 11,
  },
  uploadStatus: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 8,
    marginBottom: 12,
  },
  uploadStatusError: {
    fontSize: 12,
    color: '#B91C1C',
    marginTop: 8,
    marginBottom: 12,
  },
};

// ── Main component ───────────────────────────────────────────────────────

export default function Documents({ workspace, roles, roleLabel, mockMode = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const workspaceId = workspace?.workspaceId;

  // ── Load /documents scoped to workspace ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadDocs() {
      if (mockMode) {
        setDocs(MOCK_DOCS);
        setLoading(false);
        return;
      }

      if (!workspaceId) {
        setDocs([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const q = query(
          collection(db, 'documents'),
          where('workspaceId', '==', workspaceId),
          orderBy('uploadedAt', 'desc')
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocs(list);
      } catch (err) {
        if (cancelled) return;
        // Retry without orderBy (mirrors PlaceholderDashboard parcel-query pattern).
        // Catches missing-index errors as a soft fallback.
        // eslint-disable-next-line no-console
        console.warn('[Documents] Query failed, retrying without orderBy:', err.message);
        try {
          const q2 = query(
            collection(db, 'documents'),
            where('workspaceId', '==', workspaceId)
          );
          const snap2 = await getDocs(q2);
          if (cancelled) return;
          const list2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
          setDocs(list2);
        } catch (err2) {
          if (cancelled) return;
          setError(err2.message || 'Failed to load documents');
          setDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDocs();
    return () => { cancelled = true; };
  }, [workspaceId, mockMode]);

  // ── Upload handler ──────────────────────────────────────────────────────
  // Three-step: (1) Firebase Storage put, (2) Firestore /documents write,
  // (3) invoke ocrDocument CF (AI-08). AI-26 wiring conditional per
  // AS-S5-004 — if CF not deployed, skip without erroring.
  async function handleUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!workspaceId) {
      setUploadError('No workspace selected');
      return;
    }
    if (mockMode) {
      setUploadStatus('Mock mode — upload disabled');
      event.target.value = '';
      return;
    }

    // uploadedBy must equal request.auth.uid per /documents create rule.
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setUploadError('No authenticated user — sign in required for upload');
      return;
    }

    setUploadError(null);
    setUploadStatus(`Uploading ${file.name}…`);

    try {
      // Step 1: Storage put
      const timestamp = Date.now();
      const path = `workspaces/${workspaceId}/docs/${timestamp}_${file.name}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const downloadUrl = await getDownloadURL(ref);

      setUploadStatus('Storage upload complete. Writing Firestore record…');

      // Step 2: Firestore /documents write — canonical schema per AS-S5-001 rev
      const mediaKind = file.type.startsWith('image/') ? 'photo'
                       : file.type.startsWith('video/') ? 'video'
                       : 'document';

      const docRecord = {
        workspaceId,
        uploadedBy: uid,
        uploadedAt: serverTimestamp(),
        fileName: file.name,
        storagePath: path,
        storageUrl: downloadUrl,
        docType: '',                    // Free-text Phase 1; AI-26 normalizes Phase 2
        mediaKind,
        processingStatus: 'Created',     // Advances to Processed after ocrDocument fires
        accessRoles: ['owner_pm', 'pm_only', 'developer', 'cm'],
        // Optional fields populated by upload context (parcelId, etc.) wired later
        parcelId: null,
        originalFilename: file.name,
      };

      const docsRef = collection(db, 'documents');
      const docRef = await addDoc(docsRef, docRecord);

      setUploadStatus(`Firestore record created (${docRef.id}). Invoking OCR…`);

      // Step 3: Invoke ocrDocument CF (AI-08)
      try {
        const ocrDocument = httpsCallable(functions, 'ocrDocument');
        await ocrDocument({ storagePath: path, documentId: docRef.id });
        setUploadStatus('Upload complete. OCR fired (processingStatus advances on CF write).');
      } catch (cfErr) {
        // CF failure is non-fatal — doc is in /documents, OCR can be re-fired later
        // eslint-disable-next-line no-console
        console.warn('[Documents] ocrDocument CF invoke failed (doc still saved):', cfErr.message);
        setUploadStatus(`Upload complete. OCR invoke failed (doc saved; re-fire later): ${cfErr.message}`);
      }

      // Refresh list
      const q = query(
        collection(db, 'documents'),
        where('workspaceId', '==', workspaceId)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocs(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Documents] Upload failed:', err);
      setUploadError(err.message || 'Upload failed');
      setUploadStatus(null);
    }

    // Reset file input so same file can be re-selected if needed
    event.target.value = '';
  }

  // ── KPI computation ─────────────────────────────────────────────────────
  const total = docs.length;
  const ocrProcessed = docs.filter((d) =>
    d.processingStatus === 'Processed'
    || d.processingStatus === 'Filed'
    || d.processingStatus === 'Linked'
    || d.processingStatus === 'Archived'
  ).length;
  const pendingSig = docs.filter((d) =>
    d.signatureStatus === 'awaiting' || d.signatureStatus === 'submitted'
  ).length;
  const aiClassified = docs.filter((d) => d.aiClassification != null).length;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Banner */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Document Vault</span>
          <span style={styles.bannerTag}>XC-DOC</span>
        </div>
        <div style={styles.bannerMuted}>
          AI-08 OCR ingestion on every upload · AI-26 classification · same vault all stages
        </div>
      </div>

      {/* Page head */}
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.pageTitle}>Documents</h1>
          <p style={styles.pageSub}>
            {total} document{total === 1 ? '' : 's'} in vault · AI-08 OCR + AI-26 classifier auto-process every upload
          </p>
        </div>
        <div style={styles.pageActions}>
          <label
            style={mockMode ? styles.btnGhostDisabled : styles.btnGhost}
            htmlFor="xcdoc-upload-input"
          >
            ⎕ Upload
          </label>
          <input
            id="xcdoc-upload-input"
            type="file"
            style={styles.hiddenFileInput}
            onChange={handleUpload}
            disabled={mockMode}
          />
          <button type="button" style={styles.btnGhost} disabled>
            🔍 Search vault
          </button>
        </div>
      </div>

      {uploadStatus && <div style={styles.uploadStatus}>{uploadStatus}</div>}
      {uploadError && <div style={styles.uploadStatusError}>Upload failed: {uploadError}</div>}

      {/* XC-FILTER (S4 component, first production consumer — UI renders,
          filter-to-table wiring deferred per AS-S5-013) */}
      <XcFilterBar viewId="xc-documents" />

      {/* KPI grid */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCardAccent}>
          <div style={styles.kpiLabel}>Total documents</div>
          <div style={styles.kpiVal}>{total}</div>
          <div style={styles.kpiSub}>workspace scope</div>
        </div>
        <div style={styles.kpiCardGood}>
          <div style={styles.kpiLabel}>OCR processed</div>
          <div style={styles.kpiVal}>{ocrProcessed}</div>
          <div style={styles.kpiSub}>AI-08 fired</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>Pending sig</div>
          <div style={styles.kpiVal}>{pendingSig}</div>
          <div style={styles.kpiSub}>awaiting action</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>AI-26 classified</div>
          <div style={styles.kpiVal}>{aiClassified}</div>
          <div style={styles.kpiSub}>{aiClassified === 0 ? 'AI-26 pending' : 'type auto-tagged'}</div>
        </div>
      </div>

      {/* Document table */}
      <div style={styles.card}>
        <div style={styles.cardHead}>
          <div style={styles.cardTitle}>Document index</div>
          <div style={styles.cardSub}>Sortable · filterable · all OCR-searchable</div>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Parcel</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={styles.emptyRow}>Loading documents…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={5} style={{ ...styles.emptyRow, color: '#B91C1C' }}>
                Could not load documents: {error}
              </td></tr>
            )}
            {!loading && !error && docs.length === 0 && (
              <tr><td colSpan={5} style={styles.emptyRow}>
                No documents in vault yet. Click Upload to add the first.
              </td></tr>
            )}
            {!loading && !error && docs.map((d) => {
              const statusForPill = d.signatureStatus || d.processingStatus || 'Created';
              const { fg, bg } = statusPillColors(statusForPill);
              const dateStr = (() => {
                if (!d.uploadedAt) return '—';
                if (typeof d.uploadedAt === 'string') return d.uploadedAt;
                if (d.uploadedAt.toDate) return d.uploadedAt.toDate().toISOString().slice(0, 10);
                return '—';
              })();
              const typeLabel = d.docType || d.fileName || '(untyped)';
              const parcelLabel = d.parcelLabel || d.parcelId || '—';
              return (
                <tr key={d.id}>
                  <td style={styles.td}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{typeLabel}</div>
                    {d.fileName && d.fileName !== typeLabel && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{d.fileName}</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.mono}>{dateStr}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{parcelLabel}</div>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        background: bg, color: fg, fontSize: 11, fontWeight: 600,
                        padding: '3px 9px', borderRadius: 10, fontFamily: 'Consolas, monospace',
                      }}
                    >
                      {statusForPill}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button type="button" style={styles.btnGhost} onClick={() => {
                      if (d.storageUrl) window.open(d.storageUrl, '_blank');
                    }}>
                      Open →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
